/**
 * What the current company's plan allows, as the app sees it: the shape of
 * `org_entitlements()` (supabase/migrations/20261023130000_plan_enforcement.sql)
 * and the pure rules the UI applies to it. The server decides; this only
 * explains, warns at 80 % and offers the upgrade before a refused click.
 */
import {
  FEATURES,
  PLANS,
  allFeatures,
  czPlural,
  getPlan,
  type FeatureId,
  type PlanId,
  type PlanLimits,
} from "@/lib/pricing/plans";
import type { PlanSource } from "./orgPlan";
import type { PlanError } from "../../../supabase/functions/_shared/plan-errors";

export type { PlanError } from "../../../supabase/functions/_shared/plan-errors";
export { asPlanError } from "../../../supabase/functions/_shared/plan-errors";

export type LimitId = keyof PlanLimits;

export interface Entitlements {
  /** billing_config.enforce: while false the server refuses nothing. */
  enforced: boolean;
  plan: PlanId | null;
  status: string;
  source: PlanSource;
  readOnly: boolean;
  limits: PlanLimits | null;
  features: FeatureId[];
  usage: Record<LimitId, number> & { pendingInvitations: number; aiDocumentsToday: number };
  /** AI documents a day (a tenth of the month's, at least 50); null without a plan. */
  dailyAiLimit: number | null;
  /** The usage month, 'YYYY-MM' (Czech time). */
  period: string;
}

/** Warn from this share of a limit on (§2: soft limits). */
export const WARN_AT = 0.8;

export type MeterLevel = "ok" | "warn" | "full" | "over";

/**
 * ok below 80 %, warn from 80 %, full at the limit (nothing more can be added,
 * which is normal, e.g. Start's one company), over past it (a grandfathered or
 * downgraded company keeps what it had).
 */
export function meterLevel(used: number, max: number): MeterLevel {
  if (used > max) return "over";
  if (max <= 0 || used === max) return "full";
  return used / max >= WARN_AT ? "warn" : "ok";
}

/**
 * Whether the UI should act on the plan: billing is visible (live, or
 * previewed) AND the server enforces. Until both are on, no prompt appears.
 */
export function gatesActive(ent: Entitlements | null | undefined, billingVisible: boolean) {
  return billingVisible && !!ent?.enforced;
}

export function hasFeature(ent: Entitlements, feature: FeatureId): boolean {
  return !ent.readOnly && ent.features.includes(feature);
}

/**
 * What adding `adding` more of a limit would hit, as the server would answer
 * (users count open invitations too). null = allowed.
 */
export function limitError(ent: Entitlements, limit: LimitId, adding = 1): PlanError | null {
  if (ent.readOnly || !ent.plan || !ent.limits) return { error: "read_only", plan: null };
  const used = ent.usage[limit] + (limit === "users" ? ent.usage.pendingInvitations : 0);
  const max = ent.limits[limit];
  if (used + adding <= max) return null;
  return { error: "plan_limit", limit, plan: ent.plan, used, max };
}

export function featureError(ent: Entitlements, feature: FeatureId): PlanError | null {
  if (ent.readOnly || !ent.plan) return { error: "read_only", plan: null };
  return hasFeature(ent, feature) ? null : { error: "plan_feature", feature, plan: ent.plan };
}

/** my_entitlements(): the person's own features, over the companies they own. */
export interface MyEntitlements {
  enforced: boolean;
  plans: PlanId[];
  readOnly: boolean;
  features: FeatureId[];
}

/** Features that belong to the person, not to the selected company. */
export const PERSONAL_FEATURES: readonly FeatureId[] = ["privateMode"];

export function personalFeatureError(me: MyEntitlements, feature: FeatureId): PlanError | null {
  if (me.readOnly) return { error: "read_only", plan: null };
  if (me.features.includes(feature)) return null;
  return { error: "plan_feature", feature, plan: me.plans[0] ?? "start" };
}

/** The smallest plan that has the feature, or more of the limit than `max`. */
export function upgradePlan(err: PlanError): PlanId | null {
  if (err.error === "read_only") return null;
  if (err.error === "plan_feature") {
    const plan = PLANS.find((p) => (allFeatures(p) as string[]).includes(err.feature));
    return plan && plan.id !== err.plan ? plan.id : null;
  }
  const plan = PLANS.find((p) => p.limits[err.limit] > err.max);
  return plan?.id ?? null;
}

// ---------------------------------------------------------------------------
// Copy: the upgrade dialog and the read-only banner (cs first, then en)
// ---------------------------------------------------------------------------

export type Locale = "cs" | "en";

export interface UpgradeCopy {
  title: string;
  body: string;
  /** The button to the plans, or null when no plan offers more. */
  cta: string | null;
}

function ordinalEn(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function grouped(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

const LIMIT_NOUN: Record<LimitId, { cs: [string, string, string]; en: [string, string] }> = {
  companies: { cs: ["firmu", "firmy", "firem"], en: ["company", "companies"] },
  fioAccounts: {
    cs: ["účet u Fio", "účty u Fio", "účtů u Fio"],
    en: ["Fio account", "Fio accounts"],
  },
  users: { cs: ["uživatele", "uživatele", "uživatelů"], en: ["user", "users"] },
  aiDocumentsPerMonth: { cs: ["doklad", "doklady", "dokladů"], en: ["document", "documents"] },
};

function countCs(limit: LimitId, n: number): string {
  const [one, few, many] = LIMIT_NOUN[limit].cs;
  return `${grouped(n)} ${czPlural(n, one, few, many)}`;
}

function countEn(limit: LimitId, n: number): string {
  const [one, many] = LIMIT_NOUN[limit].en;
  return `${grouped(n)} ${n === 1 ? one : many}`;
}

export function upgradeCopy(err: PlanError, locale: Locale): UpgradeCopy {
  const target = upgradePlan(err);
  const targetPlan = target ? getPlan(target) : null;
  const cs = locale === "cs";
  const cta = targetPlan
    ? cs
      ? `Zobrazit tarif ${targetPlan.name}`
      : `See the ${targetPlan.name} plan`
    : null;

  if (err.error === "read_only") {
    return {
      title: cs ? "Firma je teď jen pro čtení" : "This company is read-only now",
      body: cs
        ? "Zkušební verze nebo předplatné skončilo. Všechno si dál prohlédnete a vyexportujete; nové platby, doklady a účty přibudou, až si vyberete tarif."
        : "The trial or the subscription has ended. You can still view and export everything; new payments, documents and accounts come back once you choose a plan.",
      cta: cs ? "Vybrat tarif" : "Choose a plan",
    };
  }

  if (err.error === "plan_feature") {
    const feature = FEATURES[err.feature as FeatureId];
    const label = feature ? (cs ? feature.label.cs : feature.label.en) : err.feature;
    const where = targetPlan?.name ?? "Firma";
    return {
      title: cs ? `Tohle umí tarif ${where}` : `This is part of the ${where} plan`,
      body: cs
        ? `${label}. Váš tarif ${getPlan(err.plan as PlanId).name} to nezahrnuje.`
        : `${label}. Your ${getPlan(err.plan as PlanId).name} plan doesn't include it.`,
      cta,
    };
  }

  const current = getPlan(err.plan as PlanId).name;
  if (err.limit === "aiDocumentsPerDay") {
    return {
      title: cs
        ? `AI dnes přečetla ${grouped(err.used)} dokladů`
        : `The AI has read ${grouped(err.used)} documents today`,
      body: cs
        ? `Víc za jeden den tarif ${current} nepustí (${grouped(err.max)} denně), aby nikdo nemohl vyčerpat měsíc za jedno odpoledne. Další doklady AI přečte zítra.`
        : `The ${current} plan reads at most ${grouped(err.max)} a day, so no one can use up the month in an afternoon. The AI reads more tomorrow.`,
      cta: null,
    };
  }
  const next = err.used + 1;
  if (err.limit === "aiDocumentsPerMonth") {
    return {
      title: cs
        ? `AI tento měsíc přečetla ${grouped(err.used)} z ${grouped(err.max)} dokladů`
        : `The AI has read ${grouped(err.used)} of ${grouped(err.max)} documents this month`,
      body: cs
        ? `To je limit tarifu ${current}. Další doklady AI přečte od 1. dne příštího měsíce${targetPlan ? `, nebo hned s tarifem ${targetPlan.name} (${countCs(err.limit, targetPlan.limits[err.limit])} měsíčně)` : ""}.`
        : `That is the ${current} plan's limit. The AI reads more from the 1st of next month${targetPlan ? `, or right away on ${targetPlan.name} (${countEn(err.limit, targetPlan.limits[err.limit])} a month)` : ""}.`,
      cta,
    };
  }

  const limit = err.limit as Exclude<LimitId, "aiDocumentsPerMonth">;
  const verbCs = { companies: "Založit", fioAccounts: "Přidat", users: "Přizvat" }[limit];
  const verbEn = { companies: "Create", fioAccounts: "Add", users: "Invite" }[err.limit];
  const nounCs = {
    companies: "firmu",
    fioAccounts: "účet u Fio",
    users: "člověka",
  }[err.limit];
  const nounEn = {
    companies: "company",
    fioAccounts: "Fio account",
    users: "person",
  }[err.limit];
  return {
    title: cs ? `${verbCs} ${next}. ${nounCs}?` : `${verbEn} a ${ordinalEn(next)} ${nounEn}?`,
    body: cs
      ? `Tarif ${current} má ${countCs(err.limit, err.max)}.${targetPlan ? ` Tarif ${targetPlan.name} umí ${grouped(targetPlan.limits[err.limit])}.` : " Víc zatím žádný tarif nemá, napište nám."}`
      : `The ${current} plan has ${countEn(err.limit, err.max)}.${targetPlan ? ` ${targetPlan.name} has ${grouped(targetPlan.limits[err.limit])}.` : " No plan has more yet; write to us."}`,
    cta,
  };
}

/** For people who don't pay: who can change the plan. */
export function notOwnerNote(locale: Locale): string {
  return locale === "cs" ? "Tarif mění vlastník firmy." : "The company's owner changes the plan.";
}

/** The read-only banner: what stopped, and what still works. */
export function readOnlyCopy(locale: Locale) {
  return locale === "cs"
    ? {
        title: "Jen pro čtení",
        stopped: "Stahování plateb, nahrávání dokladů a nové účty jsou pozastavené.",
        works: "Prohlížení i exporty pro účetní fungují dál.",
        cta: "Vybrat tarif",
      }
    : {
        title: "Read-only",
        stopped: "Payment sync, document uploads and new accounts are paused.",
        works: "Viewing and exports for your accountant still work.",
        cta: "Choose a plan",
      };
}

/** A whole-page guard's text (budget, analytics, private mode, invoicing). */
export function featurePageCopy(feature: FeatureId, locale: Locale, readOnly = false) {
  if (readOnly) {
    const r = readOnlyCopy(locale);
    return locale === "cs"
      ? { title: "Bez tarifu jen pro čtení", body: `${r.stopped} ${r.works}`, cta: r.cta }
      : { title: "Read-only without a plan", body: `${r.stopped} ${r.works}`, cta: r.cta };
  }
  const f = FEATURES[feature];
  return locale === "cs"
    ? { title: "Tohle je v tarifu Firma", body: `${f.label.cs}.`, cta: "Zobrazit tarify" }
    : { title: "This is part of the Firma plan", body: `${f.label.en}.`, cta: "See the plans" };
}

/** For someone accepting an invitation to a company whose team is full. */
export function joinRefusedCopy(err: PlanError, locale: Locale): string {
  if (err.error === "plan_limit" && err.limit === "users") {
    return locale === "cs"
      ? `Tým této firmy je plný, tarif ${getPlan(err.plan as PlanId).name} má ${countCs("users", err.max)}. Požádejte toho, kdo vás pozval, aby uvolnil místo nebo změnil tarif.`
      : `This company's team is full (${getPlan(err.plan as PlanId).name} plan: ${countEn("users", err.max)}). Ask whoever invited you to free a seat or change the plan.`;
  }
  const c = upgradeCopy(err, locale);
  return `${c.title} ${c.body}`;
}
